import React from 'react';
import { App, Button, Card, Divider, List, Space, Tag, Tooltip, Typography } from 'antd';
import { SearchOutlined, UserDeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import UserSearchModalPublic, { LdapUserInfo } from './UserSearchModalPublic';
import { handler } from '@App/settings/ApiHandler';
import { useKeycloak } from '@App/settings/keycloak';
import { useErrorModal } from '@App/components/ErrorModalProvider';
import KC from '@App/@types/keycloakTypes';

/**
 * PUT /v3/researchProjects/<uuid>/permissions - update the responsible researcher
 * and shared users without touching form data.
 */
export const updateProjectPermissions = (kc: KC.KCType, uuid: string, payload: API.PayloadType) =>
  handler<Responses.Default>({ method: 'PUT', path: `/v3/researchProjects/${uuid}/permissions`, payload }, kc!);

/**
 * Role holder payload (responsible researcher / principal investigator)
 * matching the backend contract: {"username", "fullName", "email"} or null.
 */
export const buildRoleHolderPayload = (user: LdapUserInfo | null) =>
  user
    ? {
        username: user.username,
        fullName: `${user.firstName} ${user.lastName}`.trim(),
        email: user.email,
      }
    : null;

/**
 * Payload matching the backend contract:
 * sharedWith as {"usernames": [...], "fullNames": [...]},
 * responsibleResearcher as {"username", "fullName", "email"} or null to keep the
 * current role (it is mandatory, so it can be reassigned but never removed),
 * principalInvestigator passed through unchanged (immutable, set by Grandiveeb).
 */
export const buildPermissionsPayload = (
  sharedWithUsers: LdapUserInfo[],
  responsibleResearcher: LdapUserInfo | null,
  principalInvestigator?: LdapUserInfo | null,
) => ({
  sharedWith: {
    usernames: sharedWithUsers.map((u) => u.username),
    fullNames: sharedWithUsers.map((u) => `${u.firstName} ${u.lastName}`.trim()),
  },
  responsibleResearcher: buildRoleHolderPayload(responsibleResearcher),
  principalInvestigator: buildRoleHolderPayload(principalInvestigator ?? null),
});

type ProjectAccessCardProps = {
  /**
   * Project uuid. When given, access changes can be saved on their own via the
   * permissions endpoint ("Salvesta ligipääs" button). When not given (e.g.
   * submitting a brand new project), the parent is expected to include the
   * responsible researcher and shared users together with the form data.
   */
  projectUuid?: string;
  /** Users the project is shared with (controlled by the parent) */
  sharedWithUsers: LdapUserInfo[];
  onSharedWithUsersChange: (users: LdapUserInfo[]) => void;
  /** Responsible researcher (controlled by the parent) */
  responsibleResearcher: LdapUserInfo | null;
  onResponsibleResearcherChange: (user: LdapUserInfo | null) => void;
  /** Principal investigator (vastutav täitja) of a Grandiveeb project - read-only display */
  principalInvestigator?: LdapUserInfo | null;
  /** Show the immutable principal investigator section (project has a grandiveeb_uid) */
  showPrincipalInvestigator?: boolean;
  /** Block saving until a responsible researcher has been picked */
  requireResponsibleResearcher?: boolean;
  /** Label for the permissions save button (defaults to "Salvesta ligipääs") */
  saveButtonLabel?: React.ReactNode;
  /** Extra actions rendered next to the save button (e.g. the guided flow "Esita" button) */
  extraActions?: React.ReactNode;
  /** Optional hint rendered above the action buttons */
  footerHint?: React.ReactNode;
  /** Called after the permissions were successfully saved */
  onSaved?: () => void;
};

/**
 * "Ligipääs" block: responsible researcher (vastutav uurija) and shared users,
 * both picked with the UT user search. When a project uuid is given, changes can
 * be submitted on their own via the permissions endpoint, without any form data.
 */
export const ProjectAccessCard: React.FC<ProjectAccessCardProps> = ({
  projectUuid,
  sharedWithUsers,
  onSharedWithUsersChange,
  responsibleResearcher,
  onResponsibleResearcherChange,
  principalInvestigator,
  showPrincipalInvestigator,
  requireResponsibleResearcher,
  saveButtonLabel,
  extraActions,
  footerHint,
  onSaved,
}) => {
  const [showSharedSearch, setShowSharedSearch] = React.useState<boolean>(false);
  const [showResponsibleSearch, setShowResponsibleSearch] = React.useState<boolean>(false);
  const [isSaving, setIsSaving] = React.useState<boolean>(false);

  const { keycloak } = useKeycloak();
  const { message } = App.useApp();
  const { showError } = useErrorModal();
  const { t } = useTranslation(['forms', 'local']);

  const handleSharedUserSelection = (user: LdapUserInfo) => {
    const userExists = sharedWithUsers.find((u) => u.username === user.username);
    if (userExists) {
      message.info(t('forms.info.userAlreadySelected', 'Kasutaja juba valitud'));
    } else {
      onSharedWithUsersChange([...sharedWithUsers, user]);
    }
  };

  const handleSharedUserRemove = (user: LdapUserInfo) => {
    onSharedWithUsersChange(sharedWithUsers.filter((u) => u.username !== user.username));
  };

  const handleResponsibleResearcherSelect = (user: LdapUserInfo) => {
    // Selecting a person from the search always (re)assigns the responsible researcher
    onResponsibleResearcherChange(user);
    setShowResponsibleSearch(false);
  };

  // Submit access changes (responsible researcher + shared users) without any form data
  const handleSavePermissions = () => {
    if (!projectUuid || !keycloak) return;
    if (requireResponsibleResearcher && !responsibleResearcher) {
      showError(t('forms.error.responsibleResearcherRequired', 'Vastutav uurija on kohustuslik. Määra projektile vastutav uurija.'));
      return;
    }
    setIsSaving(true);

    updateProjectPermissions(keycloak, projectUuid, {
      action: 'update' as API.ActionType,
      payload: buildPermissionsPayload(sharedWithUsers, responsibleResearcher, principalInvestigator),
    })
      .then((res) => {
        if (res.error) {
          // Already shown by the ApiHandler
          return;
        }
        // backend returns success: 'research.permissions.updated'
        message.success(t('forms.success.' + res.success, 'Juurdepääsuõigused edukalt salvestatud'));
        onSaved?.();
      })
      .catch((err) => {
        console.error('Failed to save project permissions:', err);
        message.info(t('forms.error.tryAgain', 'Palun proovi uuesti'));
      })
      .finally(() => setIsSaving(false));
  };

  // Some users are only known by username (e.g. brand new accounts), so full name
  // and e-mail may be missing - the username is always there and used as a fallback,
  // displayed in the same blue tag format as the e-mail
  const renderUserName = (user: LdapUserInfo) => {
    const fullName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
    return fullName
      ? fullName
      : <Tag color="processing" bordered={false}>{user.username}</Tag>;
  };

  const renderUserItem = (user: LdapUserInfo, onRemove: () => void) => (
    <List.Item
      extra={
        <Button
          shape={'round'}
          size={'small'}
          type={'dashed' }
          icon={<UserDeleteOutlined />}
          className={'user-action-button'}
          onClick={onRemove}
        >
          {t(`forms.btn.remove`)}
        </Button>
      }
    >
      <List.Item.Meta
        title={
          <Space>
            {renderUserName(user)}
            {user.email && <Tag color="processing" bordered={false}>{user.email}</Tag>}
          </Space>
        }
      />
    </List.Item>
  );

  return (
    <Card>
      {/* Vastutav uurija (responsible researcher): picked here, saved either with the
          form (new projects) or via the permissions endpoint (existing projects) */}
          <Typography.Title level={5} style={{ marginTop: 0 }}>
            {t('forms.title.responsibleResearcher', 'Vastutav uurija')}
            {requireResponsibleResearcher && <Typography.Text type='danger'> *</Typography.Text>}
          </Typography.Title>
          <Typography.Paragraph type='secondary' style={{ fontSize: '.9em' }}>
            {t('forms.description.responsibleResearcher', 'Määra projektile vastutav uurija.')}
          </Typography.Paragraph>
          {responsibleResearcher ? (
            <List<LdapUserInfo>
              itemLayout="horizontal"
              size={'small'}
              dataSource={[responsibleResearcher]}
              renderItem={(user) => renderUserItem(user, () => onResponsibleResearcherChange(null))}
            />
          ) : null}
          <Button
              shape={'round'}
              type={'default'}
              icon={<SearchOutlined />}
              onClick={() => setShowResponsibleSearch(true)}
              style={{ margin: '0 1rem 1rem' }}
          >
            {responsibleResearcher
              ? t('forms.btn.changeResponsibleResearcher', 'Muuda vastutavat uurijat')
              : t('forms.btn.searchResponsibleResearcher', 'Otsi vastutav uurija')}
          </Button>

          <Divider />

      {/* Vastutav täitja (principal investigator) - immutable, set by Grandiveeb */}
      {showPrincipalInvestigator && (
        <>
          <Typography.Title level={5} style={{ marginTop: 0 }}>
            {t('forms.title.principalInvestigator', 'Vastutav täitja')}
          </Typography.Title>
          <Typography.Paragraph type='secondary' style={{ fontSize: '.9em' }}>
            {t('forms.description.principalInvestigator', 'Grandiveebi projektile määratud vastutav täitja. Väli ei ole muudetav.')}
          </Typography.Paragraph>
          {principalInvestigator ? (
            <List<LdapUserInfo>
              itemLayout="horizontal"
              size={'small'}
              dataSource={[principalInvestigator]}
              renderItem={(user) => (
                <List.Item>
                  <List.Item.Meta
                    title={
                      <Space>
                        {renderUserName(user)}
                        {user.email && <Tag color="processing" bordered={false}>{user.email}</Tag>}
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          ) : (
            <Typography.Text type='secondary'>
              {t('forms.text.notSet', 'Määramata')}
            </Typography.Text>
          )}
          <Divider />
        </>
      )}

      {/* Add people to the project (sharedWith) */}
      <Typography.Title level={5} style={{ marginTop: 0 }}>
        {t('forms.title.sharedWith', 'Juurdepääsu andmine teistele kasutajatele')}
      </Typography.Title>
      <Button
          shape={'round'}
          type={'default'}
          icon={<SearchOutlined />}
          onClick={() => setShowSharedSearch(true)}
          style={{ margin: '0 1rem 1rem' }}
      >
        {t('forms.btn.searchUtUser', 'UT kasutaja otsing')}
      </Button>
      { sharedWithUsers.length > 0 ? (
        <List<LdapUserInfo>
          itemLayout="horizontal"
          size={'small'}
          dataSource={sharedWithUsers}
          renderItem={(user) => renderUserItem(user, () => handleSharedUserRemove(user))}
        />
      ) : null}

      {/* Submit / save access changes */}
      <Divider />
      {footerHint}
      <Space wrap>
        {extraActions}
        {/* Permission-only save needs an existing project */}
        {projectUuid && (
          <Tooltip
            title={t('forms.tooltip.saveAccess', 'Salvestab ainult vastutava uurija ja jagatud kasutajad. Vormi andmeid ei muudeta.')}
          >
            <Button
                shape={'round'}
                onClick={handleSavePermissions}
                loading={isSaving}
            >
              {saveButtonLabel ?? t('forms.btn.saveAccess', 'Salvesta ligipääs')}
            </Button>
          </Tooltip>
        )}
      </Space>
      {!projectUuid && !extraActions && (
        <Typography.Paragraph type='secondary' style={{ fontSize: '.9em', marginTop: '1rem', marginBottom: 0 }}>
          {t('forms.description.accessSavedWithForm', 'Valitud kasutajad ja vastutav uurija salvestatakse koos vormi esitamisega.')}
        </Typography.Paragraph>
      )}

      <UserSearchModalPublic
          visible={showSharedSearch}
          onClose={() => setShowSharedSearch(false)}
          onSelectUser={handleSharedUserSelection}
          existingUsers={sharedWithUsers}
      />

      <UserSearchModalPublic
          visible={showResponsibleSearch}
          onClose={() => setShowResponsibleSearch(false)}
          onSelectUser={handleResponsibleResearcherSelect}
      />
    </Card>
  );
};

export default ProjectAccessCard;
